<template>
  <DxPopup :visible="visible" :show-title="true" :title="$t('common.bulk_actions')" :width="'400px'" :height="'350px'" @hiding="onClose">
    <div style="display: flex; flex-direction: column; height: 100%">
      <div style="flex: 1 1 auto" v-if="!copy_finsihed && !action_is_running">
        <div class="row" v-if="groupAction === PARCEL_CULTURE_CHANGE">
          <div class="form col-lg-12">
            <label class="control-label">{{ $t("common.culture") }}</label>
            <b-form-select
              v-model="culture_uuid"
              class="form-control"
              :class="{ 'field-error': $v.culture_uuid.$error }"
              :options="culture_list"
            ></b-form-select>
          </div>
        </div>
        <div class="row" v-if="groupAction === PARCEL_GROUP_CHANGE">
          <div class="form col-lg-12">
            <label class="control-label">{{ $t("common.group") }}</label>
            <b-form-select
              v-model="group_uuid"
              class="form-control"
              :class="{ 'field-error': $v.group_uuid.$error }"
              :options="group_list"
            ></b-form-select>
          </div>
        </div>
        <div class="row" v-if="groupAction === PARCEL_COPY">
          <div class="form col-lg-12">
            <label class="control-label">{{ $t("works.parcels.copy_to_season") }}</label>
            <b-form-select
              v-model="copy_to_season_uuid"
              class="form-control"
              :class="{ 'field-error': $v.copy_to_season_uuid.$error }"
              :options="season_list"
            ></b-form-select>
          </div>
          <div class="form-check mt-3">
            <Toggle
              :notToggledText="$t('common.no')"
              :toggledText="$t('common.yes')"
              :toggleText="$t('works.parcels.copy_with_groups_relation')"
              :isActive="copy_with_groups"
              :onChange="handleToggleCopyWithGroup"
            ></Toggle>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 fw-bold" v-if="groupAction !== PARCEL_COPY">
            {{ $t("works.parcels.selected_parcels", { count: selectedParcelUuids?.length }) }}
          </div>
          <div class="col-lg-12 fw-bold" v-if="groupAction === PARCEL_COPY">
            {{ $t("works.parcels.selected_parcels_for_copy", { count: totalCount }) }}
          </div>
        </div>
      </div>
      <div style="flex: 1 1 auto" class="row" v-if="copy_finsihed">
        <div class="col-lg-12">
          <div class="alert alert-success" v-if="(copy_result?.copied_parcels ?? 0) > 0">
            {{ $t("works.parcels.copied_parcels", { count: copy_result?.copied_parcels ?? 0 }) }}
          </div>
          <div class="alert alert-warning" v-if="(copy_result?.already_exist_parcels ?? 0) > 0">
            {{ $t("works.parcels.already_exist_parcels", { count: copy_result?.already_exist_parcels ?? 0 }) }}
          </div>
        </div>
      </div>
      <div style="flex: 1 1 auto" v-if="action_is_running" class="loader-container">
        <Loader :loading="action_is_running" size="sm"></Loader>
      </div>
      <div style="display: flex; justify-content: flex-end; gap: 12px; margin-bottom: 20px">
        <button class="btn btn-outline-primary apply-btn" @click="onContinue" :disabled="action_is_running">
          {{ copy_finsihed ? $t("common.close") : $t("common.apply") }}
        </button>
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxPopup from "devextreme-vue/popup";
import { errorMessage, successMessage } from "@/helpers/common";
import { required } from "vuelidate/lib/validators";
import { buildDropdownOptions } from "@/helpers/common";
import { PARCEL_CULTURE_CHANGE, PARCEL_GROUP_CHANGE, PARCEL_COPY } from "@/helpers/bulkActionItems";
import { handleError } from "@/helpers/generalActions";
import Toggle from "@/components/Toggle.vue";
import Loader from "@/components/Loader";

export default {
  name: "GroupEdit",
  components: {
    DxPopup,
    Toggle,
    Loader,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedParcelUuids: {
      type: Array,
      default: () => [],
    },
    groupAction: {
      type: String,
      default: null,
    },
    season_uuid: {
      type: String,
      default: null,
    },
    last_payload: {
      type: Object,
      default: () => ({}),
    },
    totalCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      culture_uuid: null,
      group_uuid: null,
      copy_to_season_uuid: null,
      PARCEL_CULTURE_CHANGE,
      PARCEL_GROUP_CHANGE,
      PARCEL_COPY,
      action_is_running: false,
      copy_finsihed: false,
      copy_result: null,
      copy_with_groups: true,
    };
  },
  computed: {
    ...mapGetters({
      groups: "works-parcels/parcelGroups",
      cultures: "works-cultures/cultures",
      seasons: "seasons/seasons",
    }),
    culture_list() {
      return buildDropdownOptions(
        this.cultures,
        {
          value: "uuid",
          text: "name",
        },
        {
          value: null,
          text: this.$t("works.parcels.select_culture_to_apply"),
        }
      );
    },
    group_list() {
      return buildDropdownOptions(
        this.groups,
        {
          value: "uuid",
          text: "name",
        },
        {
          value: null,
          text: this.$t("works.parcels.select_group_to_apply"),
        }
      );
    },
    season_list() {
      return buildDropdownOptions(
        this.seasons,
        {
          value: "uuid",
          text: "name",
        },
        {
          value: null,
          text: this.$t("works.parcels.select_season_to_copy"),
        }
      );
    },
  },
  methods: {
    ...mapActions({
      getGroups: "works-parcels/getParcelGroups",
      changeParcelsCulture: "works-parcels/changeParcelsCulture",
      changeParcelsGroup: "works-parcels/changeParcelsGroup",
      parcelsCopy: "works-parcels/parcelsCopy",
    }),
    onClose() {
      this.$emit("close");
    },
    onContinue() {
      if (this.copy_finsihed) {
        this.onClose();
        return;
      }

      if (this.action_is_running) {
        return;
      }

      this.action_is_running = true;
      if (this.groupAction === this.PARCEL_CULTURE_CHANGE) {
        this.onParcelCultureApplied();
      } else if (this.groupAction === this.PARCEL_GROUP_CHANGE) {
        this.onParcelGroupApplied();
      } else if (this.groupAction === this.PARCEL_COPY) {
        this.onParcelCopyApplied();
      } else {
        errorMessage(this, this.$t("common.invalid_action"));
        this.action_is_running = false;
        return;
      }
    },
    async onParcelCultureApplied() {
      this.$v.culture_uuid.$touch();
      if (this.$v.culture_uuid.$invalid) {
        this.action_is_running = false;
        return;
      }
      const vm = this;
      this.changeParcelsCulture({
        parcels: this.selectedParcelUuids.map((uuid) => {
          return {
            uuid: uuid,
          };
        }),
        culture_uuid: this.culture_uuid,
      })
        .then((resp) => {
          successMessage(vm, resp.message);
          this.$emit("continue", true);
          this.$emit("close");
          this.action_is_running = false;
        })
        .catch((e) => {
          handleError(vm, e);
          this.action_is_running = false;
        });
    },
    async onParcelGroupApplied() {
      this.$v.group_uuid.$touch();
      if (this.$v.group_uuid.$invalid) {
        this.action_is_running = false;
        return;
      }
      const vm = this;
      this.changeParcelsGroup({
        parcels: this.selectedParcelUuids.map((uuid) => {
          return {
            uuid: uuid,
          };
        }),
        parcel_group_uuid: this.group_uuid,
      })
        .then((resp) => {
          successMessage(vm, resp.message);
          this.$emit("continue", true);
          this.$emit("close");
          this.action_is_running = false;
        })
        .catch((e) => {
          handleError(vm, e);
          this.action_is_running = false;
        });
    },
    async onParcelCopyApplied() {
      this.$v.copy_to_season_uuid.$touch();
      if (this.$v.copy_to_season_uuid.$invalid) {
        this.action_is_running = false;
        return;
      }

      if (this.copy_to_season_uuid == this.season_uuid) {
        errorMessage(this, this.$t("bulk_actions.new_season_be_different_from_old"));
        this.action_is_running = false;
        return;
      }

      const payload = {
        ...this.last_payload,
        copy_to_season_uuid: this.copy_to_season_uuid,
        copy_with_groups: this.copy_with_groups ? true : false,
      };
      payload.page = null;
      payload.limit = 100000; // Set a high limit to copy all parcels

      const vm = this;
      this.parcelsCopy(payload)
        .then((resp) => {
          this.copy_result = resp.data;
          this.action_is_running = false;
          this.copy_finsihed = true;
        })
        .catch((e) => {
          handleError(vm, e);
          this.action_is_running = false;
        });
    },
    handleToggleCopyWithGroup(isActive) {
      this.copy_with_groups = isActive;
    },
  },
  mounted() {
    if (this.groupAction === PARCEL_GROUP_CHANGE) {
      this.getGroups({
        season_uuid: this.season_uuid,
      });
    }
  },
  validations() {
    return {
      group_uuid: {
        required,
      },
      culture_uuid: {
        required,
      },
      copy_to_season_uuid: {
        required,
      },
    };
  },
};
</script>
